import { memo, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MoreVertical, Eye, Flag, Link2, ExternalLink, Bookmark } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Post } from '../model/contracts'

type PostActionMenuProps = {
  post: Post
  onHide?: () => void
  onReport?: () => void
  onSave?: (postId: string) => void
}

export const PostActionMenu = memo(function PostActionMenu({
  post,
  onHide,
  onReport,
  onSave,
}: PostActionMenuProps) {
  const { t } = useTranslation('feed')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  function handleCopyLink() {
    navigator.clipboard.writeText(post.url).then(() => {
      alert(t('postAction.linkCopied'))
    })
    setOpen(false)
  }

  function handleOpenOriginal() {
    window.open(post.url, '_blank', 'noreferrer')
    setOpen(false)
  }

  function handleHide() {
    onHide?.()
    setOpen(false)
  }

  function handleReport() {
    onReport?.()
    setOpen(false)
  }

  function handleSave() {
    onSave?.(post.id)
    setOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label={t('postAction.menu')}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors',
          'hover:bg-secondary/50 hover:text-foreground',
          open && 'bg-secondary/50 text-foreground',
        )}
      >
        <MoreVertical size={15} strokeWidth={2} />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 min-w-[168px] rounded-lg border border-border bg-card shadow-md">
          <MenuItem
            icon={Bookmark}
            label={t('save', { ns: 'saved', defaultValue: 'Хадгалах' })}
            onClick={handleSave}
          />
          <MenuItem icon={Eye} label={t('postAction.hide')} onClick={handleHide} />
          <MenuItem icon={Flag} label={t('postAction.report')} onClick={handleReport} />
          <div className="my-1 border-t border-border" />
          <MenuItem
            icon={Link2}
            label={t('postAction.copyLink')}
            onClick={handleCopyLink}
          />
          <MenuItem
            icon={ExternalLink}
            label={t('postAction.openOriginal')}
            onClick={handleOpenOriginal}
          />
        </div>
      )}
    </div>
  )
})

type MenuItemProps = {
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>
  label: string
  onClick: () => void
}

function MenuItem({ icon: Icon, label, onClick }: MenuItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-[13px] text-foreground transition-colors hover:bg-muted/40"
    >
      <Icon size={14} strokeWidth={2} className="shrink-0 text-muted-foreground" />
      {label}
    </button>
  )
}
